import useSWR, { mutate } from 'swr';
import { fetchData, fetchJson } from '~/lib/fetch';
import useUser from '~/lib/user';
import { useEventDispatch } from '~/lib/events';
import { CartProps } from '~/types/cart';
import { UserObjectProps } from '~/types/user';
import { CHECKOUT_ERRORS } from '~/lib/checkout/constants';

type ToggleCreditType = Promise<[UserObjectProps, CartProps] | [void, void]>;

interface useCartType {
  cart: CartProps;
  lineItemAdd: (
    dealId: number,
    planId?: number | null,
    couponCode?: string | null,
  ) => void;
  lineItemUpdate: (dealId: number, quantity: number) => void;
  lineItemUpdatePlan: (dealId: number, planId: number) => void;
  lineItemRemove: (dealId: number) => void;
  toggleCredits: (useCredits: boolean) => ToggleCreditType;
  applyCoupon: (couponCode: string) => any;
  unapplyCoupon: (couponCode: string) => any;
  mutateItems: () => void;
}

export function useCart(): useCartType {
  const { data: cart, mutate: mutateItems } = useSWR('/api/v2/cart/items/');
  const { mutate: mutateUser } = useUser();
  const dispatchEvent = useEventDispatch();

  const lineItemAdd = async (
    dealId: number,
    planId?: number | null,
    couponCode?: string | null,
  ) => {
    dispatchEvent('cart:adding');
    const couponParam = `?coupon=${couponCode}`;

    return fetchJson(`/api/v2/cart/add/${couponCode ? couponParam : ''}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      include: 'credentials',
      body: JSON.stringify({
        deal_id: dealId,
        plan_id: planId,
        coupon_code: couponCode,
      }),
    }).then((/*data*/) => {
      // TODO: revert back when endpoint is fixed
      return Promise.all([
        mutateUser(),
        mutateItems(),
        dispatchEvent('cart:added'),
      ]);

      // return Promise.resolve(data);
    });
  };

  const lineItemUpdate = async (dealId: number, quantity: number) => {
    return fetchJson('/api/v2/cart/modify/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      include: 'credentials',
      body: JSON.stringify({ deal_id: dealId, quantity }),
    }).then(() => Promise.all([mutateUser(), mutateItems()]));
  };

  const lineItemUpdatePlan = async (dealId: number, planId: number) => {
    return fetchJson('/api/v2/cart/modify/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      include: 'credentials',
      body: JSON.stringify({ deal_id: dealId, plan_id: planId }),
    }).then(() => Promise.all([mutateUser(), mutateItems()]));
  };

  const lineItemRemove = async (dealId: number) => {
    return fetchJson('/api/v2/cart/remove/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      include: 'credentials',
      body: JSON.stringify({ deal_id: dealId }),
    }).then(() => Promise.all([mutateUser(), mutateItems()]));
  };

  const toggleCredits = async (useCredit: boolean): ToggleCreditType => {
    const formData = new FormData();
    formData.append('use_credit', useCredit ? 'True' : 'False');
    dispatchEvent('credits:loading');

    return (
      fetchData('/cart/credit/', {
        method: 'POST',
        include: 'credentials',
        body: formData,
      })
        .then(() => Promise.all([mutateUser(), mutateItems()]))
        // Send back undefined to let the caller know that the request failed and to show an error
        // The caller destructures the response into two variables (only need the later of the two)
        .catch(() => Promise.all([undefined, undefined]))
        .finally(() => dispatchEvent('credits:reset'))
    );
  };

  // @return: Promise<string> of error message (empty string for success)
  const toggleCoupon = async (
    couponCode: string,
    apply: boolean,
  ): Promise<string> => {
    dispatchEvent('coupon:loading');

    const formData = new FormData();
    formData.append('code', couponCode);

    let err = '';

    try {
      await fetchData(`/cart/ajax/${apply ? '' : 'remove_'}coupon/`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      });
      await Promise.all([mutateUser(), mutateItems()]);
    } catch (e: any) {
      const { code } = await e.response.json();
      err = code?.length ? code[0] : CHECKOUT_ERRORS.COUPON;
    }

    dispatchEvent('coupon:reset');
    return err;
  };

  const applyCoupon = async (couponCode: string) => {
    return toggleCoupon(couponCode, true);
  };

  const unapplyCoupon = async (couponCode: string) => {
    return toggleCoupon(couponCode, false);
  };

  return {
    cart,
    lineItemAdd,
    lineItemUpdate,
    lineItemUpdatePlan,
    lineItemRemove,
    toggleCredits,
    applyCoupon,
    unapplyCoupon,
    mutateItems,
  };
}

interface useSavedCartType {
  items: any;
  saveDeal: (dealId: number) => void;
  removeSavedDeal: (wishlistSlug: string, dealId: number) => void;
  moveToCart: (dealId: number) => void;
  moveAllToCart: (wishlistId: number) => Promise<any>;
}

export function useSavedCart(): useSavedCartType {
  const { data: items, mutate: mutateItems } = useSWR(
    '/api/wishlists/saved/items/',
  );
  const { mutate: mutateUser } = useUser();

  const saveDeal = (dealId: number) => {
    fetchJson('/api/wishlists/saved/save_deal/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      include: 'credentials',
      body: JSON.stringify({ deal_id: dealId }),
    }).then(() =>
      Promise.all([mutateUser(), mutateItems(), mutate('/api/v2/cart/items/')]),
    );
  };

  const removeSavedDeal = (wishlistSlug: string, dealId: number) => {
    fetchJson(`/api/wishlists/${wishlistSlug}/remove_deal/`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      include: 'credentials',
      body: JSON.stringify({ deal_id: dealId }),
    }).then(() => Promise.all([mutateUser(), mutateItems()]));
  };

  const moveToCart = (dealId: number) => {
    fetchJson('/api/wishlists/saved/move_to_cart/', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      include: 'credentials',
      body: JSON.stringify({ deal_id: dealId }),
    }).then(() =>
      Promise.all([mutateUser(), mutateItems(), mutate('/api/v2/cart/items/')]),
    );
  };

  const moveAllToCart = (wishlistId: number): Promise<any> => {
    return fetchJson(`/api/wishlists/${wishlistId}/add_all_to_cart/`, {
      method: 'POST',
      include: 'credentials',
    }).then(() =>
      Promise.all([mutateUser(), mutateItems(), mutate('/api/v2/cart/items/')]),
    );
  };

  return {
    items,
    saveDeal,
    removeSavedDeal,
    moveToCart,
    moveAllToCart,
  };
}
